// 浏览器提示信息统一管理
export const BROWSER_TIPS = {
  // 系统内录音相关提示
  SYSTEM_AUDIO: {
    // 成功提示
    SUCCESS: {
      title: 'System Audio Recording Started',
      message: 'System audio recording has started. Please ensure your online audio or videos is playing. Do not close this tab during recording.',
      type: 'success'
    },
    
    // 浏览器不兼容
    BROWSER_INCOMPATIBLE: {
      title: 'Browser Not Supported',
      message: {
        safari: 'Safari browser does not support system audio recording. Please use Chrome browser for the best experience.',
        firefox: 'Firefox browser does not support system audio recording. Please use Chrome browser for the best experience.',
        default: 'Your current browser does not support system audio recording. Please use Chrome browser for the best experience.'
      },
      type: 'error'
    },
    
    // Web Audio API 不支持
    WEB_AUDIO_NOT_SUPPORTED: {
      title: 'Web Audio API Not Supported',
      message: 'Your browser does not support Web Audio API. Please use Chrome browser.',
      type: 'error'
    },
    
    // 屏幕共享不支持
    SCREEN_SHARE_NOT_SUPPORTED: {
      title: 'Screen Sharing Not Supported',
      message: 'Your browser does not support screen sharing or system audio recording. Please use Chrome browser.',
      type: 'error'
    },
    
    // 无效的捕获类型
    INVALID_CAPTURE_TYPE: {
      title: 'Invalid Capture Type',
      message: 'For optimal audio quality, please select only the "Chrome Tab" option. Sharing the entire screen or application window does not support audio recording.',
      type: 'warning'
    },
    
    // 用户取消屏幕共享
    USER_CANCELLED: {
      title: 'Screen Sharing Cancelled',
      message: 'You canceled the screen sharing selection. System audio recording requires screen sharing permission to capture audio.',
      type: 'warning'
    },
    
    // 没有音频轨道
    NO_AUDIO_TRACK: {
      title: 'No Audio Track',
      message: 'No audio track detected. Please make sure you checked "Share audio" option when sharing your screen.',
      type: 'error'
    },
    
    // 屏幕共享停止
    SCREEN_SHARE_STOPPED: {
      title: 'Screen Sharing Stopped',
      message: 'Screen sharing has stopped. You can now choose a different recording method or start system audio recording again.',
      type: 'info'
    }
  },
  
  // 麦克风录音相关提示
  MICROPHONE: {
    // 成功提示
    SUCCESS: {
      title: 'Microphone Recording Started',
      message: 'Microphone recording has started. Please speak into your microphone and maintain a quiet environment for best recording quality.',
      type: 'success'
    },
    
    // 权限被拒绝
    PERMISSION_DENIED: {
      title: 'Microphone Access Denied',
      message: 'Microphone access denied. Please allow microphone access in your browser settings and try again.',
      type: 'error'
    },
    
    // 设备未找到
    DEVICE_NOT_FOUND: {
      title: 'Microphone Not Found',
      message: 'No microphone device detected. Please ensure your device is properly connected and recognized by the system.',
      type: 'error'
    },
    
    // 通用错误
    GENERIC_ERROR: {
      title: 'Recording Error',
      message: 'Error starting microphone recording. Please check your device connection or browser settings and try again.',
      type: 'error'
    }
  },
  
  // 通用提示
  GENERAL: {
    // 在线课程模式说明
    ONLINE_COURSE_MODE: {
      title: 'Online Course Mode',
      message: 'Captures audio directly from a browser tab<br>ideal for online classes or web meetings',
      type: 'info'
    },
    
    // 录音失败通用提示
    RECORDING_FAILED: {
      title: 'Recording Failed',
      message: 'Failed to start recording. Please check your microphone settings and try again.',
      type: 'error'
    }
  }
};

// 推荐浏览器配置
export const RECOMMENDED_BROWSERS = {
  chrome: {
    name: 'Chrome',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/chrome/chrome-original.svg',
    url: 'https://www.google.com/chrome/'
  }
};

// 浏览器检测工具函数
export const BrowserDetector = {
  // 检测是否为Chrome浏览器
  isChrome() {
    return /chrome/i.test(navigator.userAgent) && !/edg/i.test(navigator.userAgent);
  },
  
  // 检测是否为Safari浏览器
  isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  },
  
  // 检测是否为Firefox浏览器
  isFirefox() {
    return /firefox/i.test(navigator.userAgent);
  },
  
  // 获取Safari版本
  getSafariVersion() {
    if (this.isSafari()) {
      const match = navigator.userAgent.match(/Version\/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    }
    return 0;
  },
  
  // 获取浏览器类型
  getBrowserType() {
    if (this.isChrome()) return 'chrome';
    if (this.isSafari()) return 'safari';
    if (this.isFirefox()) return 'firefox';
    return 'unknown';
  }
};

// 提示信息获取工具函数
export const TipHelper = {
  // 获取系统录音提示
  getSystemAudioTip(errorType, browserType = null) {
    const detectedBrowser = browserType || BrowserDetector.getBrowserType();
    
    switch (errorType) {
      case 'BROWSER_INCOMPATIBLE': {
        const tip = BROWSER_TIPS.SYSTEM_AUDIO.BROWSER_INCOMPATIBLE;
        return {
          ...tip,
          message: tip.message[detectedBrowser] || tip.message.default
        };
      }
      case 'WEB_AUDIO_NOT_SUPPORTED':
        return BROWSER_TIPS.SYSTEM_AUDIO.WEB_AUDIO_NOT_SUPPORTED;
      case 'SCREEN_SHARE_NOT_SUPPORTED':
        return BROWSER_TIPS.SYSTEM_AUDIO.SCREEN_SHARE_NOT_SUPPORTED;
      case 'INVALID_CAPTURE_TYPE':
        return BROWSER_TIPS.SYSTEM_AUDIO.INVALID_CAPTURE_TYPE;
      case 'USER_CANCELLED':
        return BROWSER_TIPS.SYSTEM_AUDIO.USER_CANCELLED;
      case 'NO_AUDIO_TRACK':
        return BROWSER_TIPS.SYSTEM_AUDIO.NO_AUDIO_TRACK;
      case 'SCREEN_SHARE_STOPPED':
        return BROWSER_TIPS.SYSTEM_AUDIO.SCREEN_SHARE_STOPPED;
      case 'SUCCESS':
        return BROWSER_TIPS.SYSTEM_AUDIO.SUCCESS;
      default:
        return BROWSER_TIPS.GENERAL.RECORDING_FAILED;
    }
  },
  
  // 获取麦克风录音提示
  getMicrophoneTip(errorType) {
    switch (errorType) {
      case 'PERMISSION_DENIED':
        return BROWSER_TIPS.MICROPHONE.PERMISSION_DENIED;
      case 'DEVICE_NOT_FOUND':
        return BROWSER_TIPS.MICROPHONE.DEVICE_NOT_FOUND;
      case 'SUCCESS':
        return BROWSER_TIPS.MICROPHONE.SUCCESS;
      case 'GENERIC_ERROR':
      default:
        return BROWSER_TIPS.MICROPHONE.GENERIC_ERROR;
    }
  },
  
  // 获取在线课程模式提示
  getOnlineCourseTip() {
    return BROWSER_TIPS.GENERAL.ONLINE_COURSE_MODE;
  }
};

// 浏览器兼容性检查工具
export const CompatibilityChecker = {
  // 检查系统录音兼容性
  checkSystemAudioCompatibility() {
    const result = {
      isCompatible: true,
      errorType: null,
      tip: null
    };
    
    // 检查是否为Chrome浏览器
    if (!BrowserDetector.isChrome()) {
      result.isCompatible = false;
      result.errorType = 'BROWSER_INCOMPATIBLE';
      result.tip = TipHelper.getSystemAudioTip('BROWSER_INCOMPATIBLE');
      return result;
    }
    
    // 检查Web Audio API支持
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      result.isCompatible = false;
      result.errorType = 'WEB_AUDIO_NOT_SUPPORTED';
      result.tip = TipHelper.getSystemAudioTip('WEB_AUDIO_NOT_SUPPORTED');
      return result;
    }
    
    // 检查屏幕共享API支持
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      result.isCompatible = false;
      result.errorType = 'SCREEN_SHARE_NOT_SUPPORTED';
      result.tip = TipHelper.getSystemAudioTip('SCREEN_SHARE_NOT_SUPPORTED');
      return result;
    }
    
    return result;
  },
  
  // 检查麦克风录音兼容性
  checkMicrophoneCompatibility() {
    const result = {
      isCompatible: true,
      errorType: null,
      tip: null
    };
    
    // 检查getUserMedia支持
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      result.isCompatible = false;
      result.errorType = 'GENERIC_ERROR';
      result.tip = TipHelper.getMicrophoneTip('GENERIC_ERROR');
      return result;
    }
    
    return result;
  }
};

// 工具函数：打开推荐浏览器下载页面
// export const openBrowserRecommendation = (browserType = 'chrome') => {
//   const browser = RECOMMENDED_BROWSERS[browserType];
//   if (browser && browser.url) {
//     window.open(browser.url, '_blank');
//   }
// };

// 向后兼容的导出（保持原有API）
export const RECORDING_TIPS = BROWSER_TIPS;
export const getSystemAudioTip = (type, isInvalidCaptureType, isBrowserIncompatible) => {
  if (isInvalidCaptureType) {
    return TipHelper.getSystemAudioTip('INVALID_CAPTURE_TYPE');
  } else if (isBrowserIncompatible) {
    return TipHelper.getSystemAudioTip('BROWSER_INCOMPATIBLE');
  }
  return TipHelper.getSystemAudioTip('SUCCESS');
}; 